// @ts-strict-ignore
import { useEffect, useRef } from "react";

import { useClientStyleContext } from "@remix/context/ClientStyleContext";

/**
 * This hook resets emotion styles on the client side whenever a top level ErrorBoundary is rendered.
 * Otherwise, the error pages would be unstyled
 */
const useResetEmotionStyles = ({ emotionCache }) => {
  const clientStyleData = useClientStyleContext();
  const reinjectStylesRef = useRef(true);

  // Only executed on client
  // When a top level ErrorBoundary or CatchBoundary are rendered,
  // the document head gets removed, so we have to re-create the style tags
  useEffect(() => {
    if (!reinjectStylesRef.current) {
      return;
    }

    const sheet = emotionCache.sheet;
    const existingTags = [...sheet.tags];

    sheet.flush();

    existingTags.forEach(tag => {
      // Create a new style tag with the same content and attributes
      const newTag = document.createElement("style");

      if (tag.getAttribute("data-emotion")) {
        newTag.setAttribute("data-emotion", tag.getAttribute("data-emotion") || "");
      }

      newTag.innerHTML = tag.innerHTML;

      // eslint-disable-next-line no-underscore-dangle
      if (sheet._insertTag) {
        // eslint-disable-next-line no-underscore-dangle
        sheet._insertTag(newTag);
      } else {
        // Fallback to direct DOM manipulation
        document.head.appendChild(newTag);
      }
    });

    // reset cache to re-apply global styles
    clientStyleData.reset();
    // ensure we only do this once per mount
    reinjectStylesRef.current = false;
  }, [clientStyleData, emotionCache.sheet]);
};

export { useResetEmotionStyles };
